import { Controller, useForm } from "react-hook-form";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
  Security as SecurityIcon,
  Check as CheckIcon,
  Phone as PhoneIcon,
  Telegram as TelegramIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import { useState } from "react";
import axios from "../../api/axios";
import { phoneRegex } from "../../utils/validationPattern";
import logo from "../../utils/images/ridePlus.png";

function TenantRegistration() {
  // USESTATE
  const [showPassword, setShowPassword] = useState(false);
  const [inputType, setInputType] = useState("password");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmInputType, setConfirmInputType] = useState("password");
  const [selectedAuthMethods, setSelectedAuthMethods] = useState(["phone"]);
  const [showTelegramFields, setShowTelegramFields] = useState(false);
  const [secretModalOpen, setSecretModalOpen] = useState(false);
  const [tenantSecret, setTenantSecret] = useState(null);

  // NAVIGATE & SNACKBAR
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // REACT HOOK FORM
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      logo: "",
      telegram_bot_token: "",
      telegram_bot_user_name: "",
      tenant_admin: {
        first_name: "",
        last_name: "",
        middle_name: "",
        email: "",
        phone: "",
        password: "",
        confirm_password: "",
        gender: "",
      },
    },
  });

  const password = watch("tenant_admin.password", "");
  const confirmPassword = watch("tenant_admin.confirm_password", "");

  // Fetch auth methods
  const {
    data: authMethodsData,
    isLoading: authMethodsLoading,
    error: authMethodsError,
    refetch,
  } = useQuery(["authmethods"], () => axios.get("/authmethods"), {
    select: (data) => {
      return data.data?.data?.filter((method) => method.key !== "email") || [];
    },
  });

  const authMethods = authMethodsData || [];

  // MUTATION
  const { mutate: createTenant, isLoading } = useMutation(
    (data) => {
      const { confirm_password, ...tenantAdmin } = data.tenant_admin;
      const tenantData = {
        ...data,
        preferred_auth_methods: selectedAuthMethods,
        tenant_admin: tenantAdmin,
      };

      if (!selectedAuthMethods.includes("telegram")) {
        delete tenantData.telegram_bot_token;
        delete tenantData.telegram_bot_user_name;
      }

      return axios.post("/tenants", tenantData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    {
      onSuccess: (data) => {
        const secret = data?.data?.data?.tenant?.secret;
        if (secret) {
          setTenantSecret(secret);
          setSecretModalOpen(true);
        } else {
          enqueueSnackbar("Tenant successfully registered!", {
            variant: "success",
            preventDuplicate: true,
            autoHideDuration: 3000,
          });
          navigate("/login");
        }
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Registration failed!";

        enqueueSnackbar(errorMessage, {
          variant: "error",
          preventDuplicate: true,
          autoHideDuration: 4000,
        });

        if (error?.response?.data?.error?.field_error) {
          error.response.data.error.field_error.forEach((fieldError) => {
            enqueueSnackbar(`${fieldError.name}: ${fieldError.description}`, {
              variant: "error",
              preventDuplicate: true,
              autoHideDuration: 4000,
            });
          });
        }
      },
    }
  );

  // Auth method handlers
  const handleAuthMethodToggle = (methodKey) => {
    if (methodKey === "phone") {
      enqueueSnackbar(
        "Phone authentication is required and cannot be disabled",
        {
          variant: "info",
          autoHideDuration: 3000,
        }
      );
      return;
    }

    setSelectedAuthMethods((prev) => {
      const isSelected = prev.includes(methodKey);
      if (isSelected) {
        return prev.filter((key) => key !== methodKey);
      } else {
        return [...prev, methodKey];
      }
    });

    if (methodKey === "telegram") {
      setShowTelegramFields(!selectedAuthMethods.includes("telegram"));
    }
  };

  const handleSelectAllAuthMethods = () => {
    const allMethodKeys = authMethods.map((method) => method.key);
    if (selectedAuthMethods.length === allMethodKeys.length) {
      setSelectedAuthMethods(["phone"]);
      setShowTelegramFields(false);
    } else {
      setSelectedAuthMethods(allMethodKeys);
      setShowTelegramFields(allMethodKeys.includes("telegram"));
    }
  };

  // EVENT HANDLERS & HELPERS
  const handleBack = () => {
    navigate(-1);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
    setInputType(showPassword ? "password" : "text");
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
    setConfirmInputType(showConfirmPassword ? "password" : "text");
  };

  const handleTenantRegistration = (formData) => {
    if (password !== confirmPassword) {
      enqueueSnackbar("Passwords don't match!", {
        variant: "error",
        preventDuplicate: true,
        autoHideDuration: 3000,
      });
      return;
    }

    if (selectedAuthMethods.includes("telegram")) {
      if (!formData.telegram_bot_token?.trim()) {
        enqueueSnackbar(
          "Telegram Bot Token is required when Telegram authentication is selected!",
          {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 3000,
          }
        );
        return;
      }
      if (!formData.telegram_bot_user_name?.trim()) {
        enqueueSnackbar(
          "Telegram Bot Username is required when Telegram authentication is selected!",
          {
            variant: "error",
            preventDuplicate: true,
            autoHideDuration: 3000,
          }
        );
        return;
      }
    }

    createTenant(formData);
  };

  const validateURL = (value) => {
    if (!value) return true;
    try {
      new URL(value);
      return true;
    } catch {
      return "Please enter a valid URL";
    }
  };

  // RENDER
  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 2, md: 4 },
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          background: theme => theme.palette.mode === 'light'
            ? "linear-gradient(145deg, #f5f7fa 0%, #ffffff 100%)"
            : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: { xs: 2, md: 3 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                "&:hover": {
                  backgroundColor: "primary.light",
                  color: "primary.contrastText",
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              sx={{
                width: { xs: 50, md: 60 },
                height: { xs: 35, md: 45 },
                borderRadius: 0,
                mr: { xs: 1, sm: 2 },
              }}
              src={logo}
              alt="Logo"
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
              }}
            >
              Tenant Registration
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            mb: { xs: 2, md: 3 },
          }}
        >
          <form onSubmit={handleSubmit(handleTenantRegistration)}>
            {/* Tenant Information */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BusinessIcon
                    sx={{ mr: 1, color: "primary.main", fontSize: 28 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                    }}
                  >
                    Tenant Information
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{
                        required: "Tenant name is required",
                        minLength: {
                          value: 2,
                          message: "Name must be at least 2 characters",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Tenant Name"
                          fullWidth
                          size="small"
                          error={!!errors.name}
                          helperText={errors.name?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="logo"
                      control={control}
                      rules={{
                        validate: validateURL,
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Logo URL (Optional)"
                          fullWidth
                          size="small"
                          error={!!errors.logo}
                          helperText={errors.logo?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Authentication Methods */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    flexDirection: { xs: "column", sm: "row" },
                    gap: { xs: 2, sm: 0 },
                    alignItems: { xs: "flex-start", sm: "center" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <SecurityIcon
                      sx={{
                        mr: 1,
                        color: "primary.main",
                        fontSize: { xs: 24, md: 28 },
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      }}
                    >
                      Authentication Methods
                    </Typography>
                  </Box>

                  {authMethods.length > 0 && (
                    <Button
                      size="small"
                      onClick={handleSelectAllAuthMethods}
                      disabled={authMethodsLoading}
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        fontWeight: 600,
                        alignSelf: { xs: "flex-start", sm: "center" },
                      }}
                    >
                      {selectedAuthMethods.length === authMethods.length
                        ? "Deselect All"
                        : "Select All"}
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 3 }} />

                {authMethodsLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 4,
                    }}
                  >
                    <CircularProgress size={32} />
                  </Box>
                ) : authMethodsError ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: theme => theme.palette.mode === 'light' ? "error.50" : alpha(theme.palette.error.main, 0.1),
                      borderColor: "error.light",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      color="error"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Failed to load authentication methods
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => refetch()}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        textTransform: "none",
                        mt: 1,
                      }}
                    >
                      Retry
                    </Button>
                  </Paper>
                ) : authMethods.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      textAlign: "center",
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    <Typography color="text.secondary">
                      No authentication methods available
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      {authMethods?.map((method) => {
                        const isSelected = selectedAuthMethods.includes(
                          method.key
                        );
                        const isRequired = method.key === "phone";
                        const isDisabled = method.key === "phone";

                        return (
                          <Grid item xs={12} sm={6} md={4} key={method.id}>
                            <Paper
                              elevation={isSelected ? 4 : 0}
                              variant={isSelected ? "elevation" : "outlined"}
                              onClick={() =>
                                !isDisabled &&
                                handleAuthMethodToggle(method.key)
                              }
                              sx={{
                                p: 2,
                                height: { xs: "auto", sm: 200 },
                                minHeight: 200,
                                display: "flex",
                                flexDirection: "column",
                                cursor: isDisabled ? "default" : "pointer",
                                border: isSelected ? 2 : 1,
                                borderColor: isSelected
                                  ? "primary.main"
                                  : "divider",
                                bgcolor: isSelected
                                  ? theme => theme.palette.mode === 'light' ? "primary.lighter" : alpha(theme.palette.primary.main, 0.1)
                                  : "background.paper",
                                opacity: isDisabled ? 0.7 : 1,
                                transition:
                                  "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: isSelected
                                  ? "translateY(-2px)"
                                  : "none",
                                "&:hover": !isDisabled
                                  ? {
                                    transform: "translateY(-4px)",
                                    boxShadow: 4,
                                    borderColor: "primary.main",
                                  }
                                  : {},
                                position: "relative",
                                overflow: "hidden",
                                borderRadius: 2,
                              }}
                            >
                              {isSelected && (
                                <>
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      right: 0,
                                      width: 0,
                                      height: 0,
                                      borderTop: "24px solid",
                                      borderRight: "24px solid",
                                      borderColor:
                                        "primary.main transparent transparent transparent",
                                    }}
                                  />
                                  <CheckIcon
                                    sx={{
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                      fontSize: 16,
                                      bgcolor: "background.paper",
                                    }}
                                  />
                                </>
                              )}

                              {isRequired && (
                                <Chip
                                  label="Required"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    position: "absolute",
                                    top: 4,
                                    left: 8,
                                    fontSize: "0.6rem",
                                    fontWeight: 600,
                                    height: 20,
                                  }}
                                />
                              )}

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                  mt: 2,
                                }}
                              >
                                {method.key === "phone" && (
                                  <PhoneIcon
                                    sx={{
                                      fontSize: 32,
                                      mr: 2,
                                      color: isSelected
                                        ? "primary.main"
                                        : "action.active",
                                    }}
                                  />
                                )}
                                {method.key === "telegram" && (
                                  <TelegramIcon
                                    sx={{
                                      fontSize: 32,
                                      mr: 2,
                                      color: isSelected
                                        ? "primary.main"
                                        : "action.active",
                                    }}
                                  />
                                )}
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 600,
                                    color: isDisabled
                                      ? "text.disabled"
                                      : "text.primary",
                                  }}
                                >
                                  {method.name}
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: isDisabled
                                    ? "text.disabled"
                                    : "text.secondary",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.4,
                                  flex: 1,
                                  mb: 0.5,
                                }}
                              >
                                {method.key === "phone" &&
                                  "Users can sign in using their phone number with OTP verification"}
                                {method.key === "telegram" &&
                                  "Users can sign in using their Telegram account"}
                              </Typography>

                              {isRequired && (
                                <Typography
                                  variant="caption"
                                  color="primary"
                                  sx={{
                                    display: "block",
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  This method is required for all tenants
                                </Typography>
                              )}
                            </Paper>
                          </Grid>
                        );
                      })}
                    </Grid>

                    {/* Telegram Configuration */}
                    {showTelegramFields && (
                      <Box
                        sx={{
                          mt: 3,
                          p: 3,
                          bgcolor: theme => theme.palette.mode === 'light' ? "warning.50" : alpha(theme.palette.warning.main, 0.05),
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: theme => theme.palette.mode === 'light' ? "warning.200" : alpha(theme.palette.warning.main, 0.3),
                          boxShadow: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            color: theme => theme.palette.mode === 'light' ? "warning.800" : "warning.main",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                          }}
                        >
                          <TelegramIcon sx={{ mr: 1, fontSize: 28 }} />
                          Telegram Configuration
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme => theme.palette.mode === 'light' ? "warning.800" : "text.secondary",
                            mb: 2,
                            lineHeight: 1.5,
                          }}
                        >
                          Please provide your Telegram Bot credentials to enable
                          Telegram authentication
                        </Typography>

                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Controller
                              name="telegram_bot_token"
                              control={control}
                              rules={{
                                required: showTelegramFields
                                  ? "Telegram Bot Token is required"
                                  : false,
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Telegram Bot Token"
                                  fullWidth
                                  size="small"
                                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
                                  error={!!errors.telegram_bot_token}
                                  helperText={
                                    errors.telegram_bot_token?.message ||
                                    "Get this from @BotFather on Telegram"
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Controller
                              name="telegram_bot_user_name"
                              control={control}
                              rules={{
                                required: showTelegramFields
                                  ? "Telegram Bot Username is required"
                                  : false,
                              }}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label="Telegram Bot Username"
                                  fullWidth
                                  size="small"
                                  placeholder="my_awesome_bot"
                                  error={!!errors.telegram_bot_user_name}
                                  helperText={
                                    errors.telegram_bot_user_name?.message ||
                                    "Your bot's username on Telegram"
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {/* Selection Summary */}
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: "success.50",
                        borderRadius: 2,
                        border: 2,
                        borderColor: "success.200",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "success.800",
                          fontWeight: 600,
                        }}
                      >
                        <strong>{selectedAuthMethods.length}</strong>{" "}
                        authentication method
                        {selectedAuthMethods.length > 1 ? "s" : ""} selected
                        {selectedAuthMethods.includes("phone") &&
                          " (Phone is required)"}
                      </Typography>
                    </Box>
                  </>
                )}

                {/* Helper Text */}
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 2,
                    lineHeight: 1.6,
                  }}
                >
                  Select the authentication methods you want to enable for your
                  tenant. Phone authentication is required for all tenants.
                  Additional methods can be configured as needed.
                </Typography>
              </CardContent>
            </Card>

            {/* Tenant Admin Information */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <PersonIcon
                    sx={{ mr: 1, color: "primary.main", fontSize: 28 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                    }}
                  >
                    Tenant Administrator
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Controller
                      name="tenant_admin.first_name"
                      control={control}
                      rules={{
                        required: "First name is required",
                        minLength: {
                          value: 2,
                          message: "First name must be at least 2 characters",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="First Name"
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.first_name}
                          helperText={errors.tenant_admin?.first_name?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Controller
                      name="tenant_admin.middle_name"
                      control={control}
                      rules={{
                        required: "Middle name is required",
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Middle Name"
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.middle_name}
                          helperText={errors.tenant_admin?.middle_name?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Controller
                      name="tenant_admin.last_name"
                      control={control}
                      rules={{
                        required: "Last name is required",
                        minLength: {
                          value: 2,
                          message: "Last name must be at least 2 characters",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Last Name"
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.last_name}
                          helperText={errors.tenant_admin?.last_name?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="tenant_admin.email"
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Email"
                          type="email"
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.email}
                          helperText={errors.tenant_admin?.email?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="tenant_admin.phone"
                      control={control}
                      rules={{
                        required: "Phone number is required",
                        pattern: {
                          value: phoneRegex,
                          message: "Invalid phone number format",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Phone Number"
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.phone}
                          helperText={errors.tenant_admin?.phone?.message}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="tenant_admin.gender"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Gender (Optional)"
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        >
                          <MenuItem value="">Select Gender</MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="tenant_admin.password"
                      control={control}
                      rules={{
                        required: "Password is required",
                        minLength: {
                          value: 8,
                          message: "Password must be at least 8 characters",
                        },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                          message:
                            "Password must contain uppercase, lowercase, and number",
                        },
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Password"
                          type={inputType}
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.password}
                          helperText={errors.tenant_admin?.password?.message}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleTogglePasswordVisibility}
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                  }}
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Controller
                      name="tenant_admin.confirm_password"
                      control={control}
                      rules={{
                        required: "Please confirm your password",
                        validate: (value) =>
                          value === password || "Passwords don't match",
                      }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Confirm Password"
                          type={confirmInputType}
                          fullWidth
                          size="small"
                          error={!!errors.tenant_admin?.confirm_password}
                          helperText={
                            errors.tenant_admin?.confirm_password?.message
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={
                                    handleToggleConfirmPasswordVisibility
                                  }
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "action.hover",
                                    },
                                  }}
                                >
                                  {showConfirmPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {/* Submit Button */}
            <Box
              sx={{
                flexShrink: 0,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
                backgroundColor: "background.paper",
                position: "sticky",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 999,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", sm: "flex-end" },
                  gap: 2,
                  pt: 2,
                  flexDirection: { xs: "column-reverse", sm: "row" },
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={isLoading}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    minWidth: { xs: "100%", sm: 120 },
                    width: { xs: "100%", sm: "auto" },
                    "&:hover": {
                      transform: { xs: "none", sm: "translateY(-1px)" },
                      boxShadow: { xs: 0, sm: 2 },
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  size="small"
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <BusinessIcon />
                    )
                  }
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    minWidth: { xs: "100%", sm: 200 },
                    width: { xs: "100%", sm: "auto" },
                    background: "primary.main",
                    boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                    "&:hover": {
                      transform: { xs: "none", sm: "translateY(-1px)" },
                      boxShadow: {
                        xs: "0 3px 5px 2px rgba(33, 203, 243, .3)",
                        sm: "0 4px 8px 2px rgba(33, 203, 243, .4)",
                      },
                    },
                    "&:disabled": {
                      background: "grey.300",
                    },
                  }}
                >
                  {isLoading ? "Creating Tenant..." : "Register Tenant"}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </Paper>

      {/* Tenant Secret Modal */}
      <Dialog open={secretModalOpen} onClose={() => setSecretModalOpen(false)}>
        <DialogTitle>Tenant Secret</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Save this secret now. It will not be shown again.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography sx={{ fontWeight: "bold", mr: 1 }}>
              Tenant Secret:
            </Typography>
            <Typography sx={{ wordBreak: "break-all" }}>
              {tenantSecret}
            </Typography>
            <Tooltip title="Copy">
              <IconButton
                size="small"
                onClick={() =>
                  navigator.clipboard.writeText(tenantSecret || "")
                }
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              setSecretModalOpen(false);
              enqueueSnackbar("Tenant successfully registered!", {
                variant: "success",
                preventDuplicate: true,
                autoHideDuration: 3000,
              });
              navigate("/login");
            }}
          >
            I saved it
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TenantRegistration;
